import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Link } from 'gatsby'
import React, { useEffect } from 'react'
import { useWindowSize } from 'react-use'
import { useLink } from '../../hooks/useLink'
import InlinePlzTool from '../organisms/InlinePlzTool'

const M01HeaderTable = ({ data, mb = 8 }) => (
  <div className={`max-w-full mb-${mb} divide-y divide-gray-4`}>
    {data.map(({ icon, text }) => (
      <div key={text} className="flex items-center justify-start py-2.5">
        <img src={icon[0]?.url} alt="" className="mr-1.5" /> <div>{text}</div>
      </div>
    ))}
  </div>
)

const M01Header = ({
  heading,
  copy,
  image,
  svgImageContent,
  svgImageScript,
  svgImageContentMobile,
  svgImageScriptMobile,
  video,
  videoMobile,
  beginningOfTheVideoLoop,
  copy2,
  link1,
  link2,
  link3,
  dataTable,
  footerText,
  hasPlzTool,
  dataTable2,
  cardImage,
  cardCopy,
  anchorLinkId,
}) => {
  const { width: windowWidth } = useWindowSize(
    typeof window !== 'undefined' ? window.innerWidth : 640,
  )
  const isMobile = windowWidth < 768
  const { label: link1label, url: link1url } = useLink(link1)
  const { label: link2label, url: link2url } = useLink(link2)
  const { label: link3label, url: link3url } = useLink(link3)

  const hasLink1 = link1?.type
  const hasLink2 = link2?.type
  const hasLink3 = link3?.type

  const showCart = cardImage && cardCopy
  const showMiddleColumn =
    dataTable?.length || (showCart && !!dataTable2?.length)
  const showLastColumn =
    showCart || footerText || (!showCart && !!dataTable2?.length)
  const hasImage = (image && image[0]) || svgImageContent || (video && video[0])
  const hasMobileVideo = videoMobile && videoMobile[0]

  const onVideoEnded = ev => {
    if (beginningOfTheVideoLoop && beginningOfTheVideoLoop >= 0) {
      ev.target.currentTime = beginningOfTheVideoLoop
      ev.target.play()
    }
  }

  const showCopySection =
    showLastColumn ||
    showMiddleColumn ||
    copy ||
    copy2 ||
    hasLink1 ||
    hasLink2 ||
    hasLink3 ||
    hasPlzTool

  const onlyHeader = !(
    (image && image.length) ||
    svgImageContent ||
    svgImageContentMobile ||
    (video && video[0]) ||
    showCopySection
  )

  useEffect(() => {
    if (svgImageScript) {
      const executeScript = new Function(svgImageScript)
      executeScript()
    }

    if (svgImageScriptMobile) {
      const executeScriptMobile = new Function(svgImageScriptMobile)
      executeScriptMobile()
    }
  })

  return (
    <div
      id={anchorLinkId}
      className={`container ${hasImage ? '-mt-16' : 'my-12 sm:my-20 lg:my-24'} ${
        onlyHeader ? 'mb-12 md:-mb-28 lg:-mb-36 xl:-mb-36 mt-0 md:mt-0 max-w-[780px]' : ''
      }`}
    >
      {((image && image.length > 0) ||
        svgImageContent ||
        svgImageContentMobile ||
        (video && video[0])) && (
        <div className="row-type-m -z-1">
          <div className="col-1-type-m">
            <div className="-mx-[26px] sm:mx-0">
              {image && (
                <img src={image[0]?.url} alt="" className="w-full h-auto" />
              )}
              {svgImageContent && (
                <div
                  className="hidden w-full u-has-svg md:block"
                  dangerouslySetInnerHTML={{ __html: svgImageContent }}
                ></div>
              )}
              {svgImageContentMobile && (
                <div className="block w-full u-has-svg md:hidden"></div>
              )}
              {video && video[0] && (
                <>
                  {isMobile && hasMobileVideo ? (
                    <video
                      width="100%"
                      height="auto"
                      muted
                      playsInline
                      autoPlay="autoplay"
                      fetchpriority="high"
                      onEnded={onVideoEnded}
                    >
                      <source
                        src={
                          videoMobile.find(
                            ({ extension }) => extension === 'mp4',
                          )?.url
                        }
                        type='video/mp4; codecs="hvc1"'
                      />
                      <source
                        src={
                          videoMobile.find(
                            ({ extension }) => extension === 'webm',
                          )?.url
                        }
                        type="video/webm"
                      />
                    </video>
                  ) : (
                    <video
                      width="100%"
                      height="auto"
                      muted
                      playsInline
                      autoPlay="autoplay"
                      fetchPriority="high"
                      onEnded={onVideoEnded}
                    >
                      <source
                        src={
                          video.find(({ extension }) => extension === 'mp4')
                            ?.url
                        }
                        type='video/mp4; codecs="hvc1"'
                      />
                      <source
                        src={
                          video.find(({ extension }) => extension === 'webm')
                            ?.url
                        }
                        type="video/webm"
                      />
                    </video>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={`row-type-m ${
          !onlyHeader ? '-mt-1 md:-mt-3 lg:-mt-8 xl:-mt-10' : ''
        } transform-gpu`}
      >
        <div className="col-2-type-m">
          <h1 className={`${!onlyHeader ? 'mb-4 xl:mb-5' : ''} u-h0`}>{heading}</h1>
        </div>
      </div>
      {showCopySection && (
        <div className="row-type-m u-p">
          <div
            className={`${
              showLastColumn && showMiddleColumn
                ? 'col-3-type-m'
                : 'col-6-type-m'
            } column-indent pr-5 sm:pr-7 md:pr-0`}
          >
            <div className="mb-8">
              <div
                className="link-wrapper"
                dangerouslySetInnerHTML={{ __html: copy }}
              />
              {(hasLink1 || hasLink2) && (
                <div className="mt-6 space-y-4">
                  {hasLink1 && (
                    <div>
                      <Link to={link1url} className="u-btn">
                        {link1label}
                      </Link>
                    </div>
                  )}
                  {hasLink2 && (
                    <div>
                      <Link to={link2url} className="u-btn">
                        {link2label}
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {copy2 && (
                <div
                  className="mt-4 link-wrapper"
                  dangerouslySetInnerHTML={{ __html: copy2 }}
                />
              )}
              {hasLink3 && (
                <div className="mt-5 xl:mt-6">
                  <Link to={link3url} className="u-btn">
                    {link3label}
                  </Link>
                </div>
              )}
            </div>
            {hasPlzTool && (
              <div className="mb-8">
                <QueryClientProvider client={new QueryClient()}>
                  <InlinePlzTool />
                </QueryClientProvider>
              </div>
            )}
          </div>
          {showMiddleColumn && (
            <div
              className={`${
                showLastColumn ? 'col-4-type-m' : 'col-7-type-m'
              } pl-5 sm:pl-7 md:pl-8 lg:pl-0 pr-5 sm:pr-0 md:pr-8 lg:pr-0`}
            >
              {dataTable && <M01HeaderTable data={dataTable} />}

              {showCart && dataTable2 && <M01HeaderTable data={dataTable2} />}
            </div>
          )}
          {showLastColumn && (
            <div
              className={`${
                showMiddleColumn ? 'col-5-type-m' : 'col-7-type-m'
              } pr-5 sm:pr-7 md:pr-8 lg:pr-0 pl-5 sm:pl-0`}
            >
              {!showCart && dataTable2 && <M01HeaderTable data={dataTable2} />}

              {footerText && (
                <div
                  className={`link-wrapper ${hasPlzTool ? 'mb-4' : 'mb-8'}`}
                  dangerouslySetInnerHTML={{ __html: footerText }}
                />
              )}

              {showCart && (
                <div className="flex items-center w-full px-6 py-3 space-x-5 bg-white rounded-[5px] shadow-box">
                  <img
                    src={cardImage[0]?.url}
                    alt=""
                    className="w-auto h-[90px] flex-shrink-0"
                  />
                  <div
                    className="u-p-small link-wrapper"
                    dangerouslySetInnerHTML={{ __html: cardCopy }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default M01Header
